import React, { ReactNode, forwardRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useProductServiceApi } from "@api/hooks/productService.hook";
import {
    ProductTileMapper,
    ProductTileModel,
    ProductsMapper,
} from "@api/mappers/products.mapper";

import { stringToBoolean } from "@utils/attributes";
import { ProductFieldSet } from "@utils/enums";

import { useThunkDispatch } from "@redux/dispatcher";
import { load as loadCart } from "@redux/modules/api/data/cart/cart.module";
import {
    getCart,
    isSuccessfullyLoaded,
} from "@redux/modules/api/data/cart/cart.selectors";

import ProductTileComponent from "@components/Product/ProductTile/productTile.component";
import { ProductTileConfig } from "@components/Product/ProductTile/productTile.config";

type ProductTileProps = {
    sku: string;
    product: ProductTileModel;
    allowInstantAddToCart: string | boolean;
    extraButtons?: () => ReactNode;
    cqDataPath?: string;
    children?: ReactNode;
    lazyLoadImage?: boolean;
    productTileConfig: ProductTileConfig;
    analyticsContext?: {
        placement: number;
        list: string;
    };
    onClickTrack?: () => void;
    className?: string;
};

const ProductTile = forwardRef<HTMLAnchorElement | null, ProductTileProps>(
    (
        {
            sku,
            product,
            allowInstantAddToCart,
            extraButtons,
            cqDataPath,
            children,
            lazyLoadImage,
            productTileConfig,
            analyticsContext,
            onClickTrack,
            className,
        }: ProductTileProps,
        ref,
    ) => {
        const dispatch = useThunkDispatch();
        const cartIsLoaded = useSelector(isSuccessfullyLoaded);
        const cart = useSelector(getCart);

        const [clientSideProduct, setClientSideProduct] = useState(product);
        const [{}, loadProducts] = useProductServiceApi(
            {
                url: `/products`,
                method: "GET",
                mapper: ProductsMapper(ProductTileMapper),
            },
            {
                manual: true,
                useCache: false,
                autoCancel: false,
            },
        );

        useEffect(() => {
            const isClientSideRender = !!cqDataPath;
            if (isClientSideRender) {
                getProductData(sku);
                if (!cartIsLoaded) dispatch(loadCart());
            }
        }, []);

        const getProductData = (sku: string) => {
            if (sku) {
                loadProducts({
                    params: {
                        sku,
                        fields: ProductFieldSet.ProductTile,
                    },
                }).then(({ data }) => {
                    const productData = data[0];
                    setClientSideProduct(productData);
                });
            }
        };

        const handleExtraOnClick = () => {
            if (clientSideProduct.url) {
                location.assign(clientSideProduct.url);
            }
        };

        if (!clientSideProduct) {
            return null;
        }

        return (
            <ProductTileComponent
                extraButtons={extraButtons}
                className={className}
                product={product}
                allowInstantAddToCart={stringToBoolean(
                    `${allowInstantAddToCart}`,
                )}
                cart={cart || undefined}
                extraOnClick={handleExtraOnClick}
                lazyLoadImage={lazyLoadImage}
                productTileConfig={productTileConfig}
                ref={ref}
                analyticsContext={analyticsContext}
                onClickTracking={onClickTrack}
            >
                {children}
            </ProductTileComponent>
        );
    },
);

export { ProductTile };
